import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { products } from '../mock';

export const Products = () => {
  const { t } = useTranslation(['products', 'common']);
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', label: t('filters.all') },
    { id: 'coffee', label: t('filters.coffee') },
    { id: 'honey', label: t('filters.honey') },
    { id: 'whisky', label: t('filters.whisky') },
    { id: 'tea', label: t('filters.tea') },
    { id: 'cosmetics', label: t('filters.cosmetics') }
  ];

  const filteredProducts = activeFilter === 'all' 
    ? products 
    : products.filter(p => p.category === activeFilter);

  return (
    <div className="bg-[#F6F1EA]">
      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_miela-luxury/artifacts/c1t3qshb_image.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            {t('page.title')}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {t('page.subtitle')}
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 px-6 bg-white border-b border-[#E8DDD0]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === filter.id
                    ? 'bg-[#B08D57] text-white shadow-lg transform scale-105'
                    : 'bg-[#F6F1EA] text-[#3A312A] hover:bg-[#E8DDD0]'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <Card
                key={product.id}
                className="group border-none shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2"
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                  <Badge className="absolute top-4 right-4 bg-[#B08D57] text-white border-none">
                    {product.origin}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-[#3A312A] mb-2">
                    {product.translationKey ? t(`productsList.${product.translationKey}.name`) : product.name}
                  </h3>
                  <p className="text-[#3A312A]/70 mb-4 leading-relaxed">
                    {product.translationKey ? t(`productsList.${product.translationKey}.description`) : product.description}
                  </p>
                  
                  {product.flavor && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-[#3A312A] mb-2">{t('product.tastingNotes')}:</p>
                      <div className="flex flex-wrap gap-2">
                        {product.flavor.map((note) => (
                          <span
                            key={note}
                            className="px-3 py-1 bg-[#F6F1EA] text-[#3A312A] text-xs rounded-full"
                          >
                            {note}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {product.benefits && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-[#3A312A] mb-2">{t('product.benefits')}:</p>
                      <div className="flex flex-wrap gap-2">
                        {product.benefits.map((benefit) => (
                          <span
                            key={benefit}
                            className="px-3 py-1 bg-[#F6F1EA] text-[#3A312A] text-xs rounded-full"
                          >
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {product.ingredients && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-[#3A312A] mb-2">{t('product.keyIngredients')}:</p>
                      <div className="flex flex-wrap gap-2">
                        {product.ingredients.map((ingredient) => (
                          <span
                            key={ingredient}
                            className="px-3 py-1 bg-[#F6F1EA] text-[#3A312A] text-xs rounded-full"
                          >
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-[#E8DDD0]">
                    <p className="text-xl font-bold text-[#B08D57]">{product.price}</p>
                    <button className="px-4 py-2 bg-[#3A312A] text-white rounded-lg hover:bg-[#1E1B18] transition-colors duration-300 text-sm font-medium">
                      {t('product.inquire')}
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#3A312A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
            {t('page.wholesaleTitle')}
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            {t('page.wholesaleDescription')}
          </p>
          <button className="bg-[#B08D57] hover:bg-[#8A6A3B] text-white px-10 py-4 rounded-lg font-medium text-lg transition-all duration-300 transform hover:scale-105">
            {t('page.wholesaleButton')}
          </button>
        </div>
      </section>
    </div>
  );
};